import { useState, useEffect } from 'react';
import { Search, Filter } from 'lucide-react';
import { Input } from '@/components/ui/input';
import Navigation from '@/components/Navigation';
import CategoryCard from '@/components/CategoryCard';
import AudioCard from '@/components/AudioCard';
import WaveLoader from '@/components/WaveLoader';
import { getCategories, getTracks, getFavorites } from '@/lib/api';
import { toast } from 'sonner';

export default function LibraryPage() {
  const [categories, setCategories] = useState([]);
  const [tracks, setTracks] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [catRes, tracksRes, favRes] = await Promise.all([
        getCategories(),
        getTracks(),
        getFavorites(),
      ]);

      setCategories(catRes.data);
      setTracks(tracksRes.data);
      setFavorites(favRes.data.map(t => t.id));
    } catch (error) {
      console.error('Error loading library:', error);
      toast.error('Failed to load library');
    } finally {
      setLoading(false);
    }
  };

  const activeSlugs = categories.filter(c => c.active).map(c => c.slug);

  const filteredTracks = tracks.filter(track =>
    track.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    track.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleFavoriteChange = (trackId, isFavorite) => {
    if (isFavorite) {
      setFavorites([...favorites, trackId]);
    } else {
      setFavorites(favorites.filter(id => id !== trackId));
    }
  };

  if (loading) {
    return <WaveLoader />;
  }

  return (
    <div className="min-h-screen bg-zinc-950 pb-24 md:pb-8 md:pt-24">
      <Navigation />

      <main className="container mx-auto px-4 md:px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-serif text-white mb-2">Explore</h1>
          <p className="text-zinc-500">Discover meditations, affirmations, and soundscapes</p>
        </div>

        {/* Search Bar */}
        <div className="relative mb-10" data-testid="search-bar">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
          <Input
            type="text"
            placeholder="Search tracks..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full bg-zinc-900/50 border-white/5 rounded-xl pl-12 pr-4 py-3 text-white placeholder:text-zinc-600 focus:border-violet-500/30 focus:ring-violet-500/20"
            data-testid="search-input"
          />
        </div>

        {/* Categories Section */}
        {!searchQuery && (
          <section className="mb-12" data-testid="categories-grid">
            <h2 className="text-xl font-serif text-white mb-6">Categories</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
              {categories.map((category, index) => (
                <CategoryCard key={category.slug} category={category} index={index} comingSoon={!category.active} />
              ))}
            </div>
          </section>
        )}

        {/* All Tracks / Search Results */}
        <section data-testid="tracks-grid">
          <h2 className="text-xl font-serif text-white mb-6">
            {searchQuery ? `Results for "${searchQuery}"` : 'All Tracks'}
          </h2>
          
          {filteredTracks.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-zinc-500">No tracks found matching your search.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
              {filteredTracks.map((track) => (
                <AudioCard 
                  key={track.id} 
                  track={track}
                  isFavorite={favorites.includes(track.id)}
                  onFavoriteChange={handleFavoriteChange}
                  comingSoon={!activeSlugs.includes(track.category)}
                />
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
